import { create } from 'zustand'
import { relay } from '../lib/relay'

interface ConnectionState {
  channelId: string
  channelKey: string
  host: string
  desktopName: string
  connected: boolean
  reconnecting: boolean
  paired: boolean

  setPairingInfo: (channelId: string, channelKey: string, host: string) => void
  setDesktopName: (name: string) => void
  setConnected: (connected: boolean) => void
  setReconnecting: (reconnecting: boolean) => void
  connect: () => Promise<void>
  disconnect: () => void
  unpair: () => void
}

const STORAGE_KEY = 'codeflows-connection'

function loadPersisted(): Partial<ConnectionState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      return {
        channelId: data.channelId || '',
        channelKey: data.channelKey || '',
        host: data.host || '',
        desktopName: data.desktopName || '',
        paired: !!(data.channelId && data.channelKey && data.host),
      }
    }
  } catch {
    // Ignore parse errors
  }
  return {}
}

function persist(state: { channelId: string; channelKey: string; host: string; desktopName: string }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      channelId: state.channelId,
      channelKey: state.channelKey,
      host: state.host,
      desktopName: state.desktopName,
    }))
  } catch {
    // Ignore storage errors
  }
}

const persisted = loadPersisted()

export const useConnectionStore = create<ConnectionState>((set, get) => {
  // Set up relay connection change handler
  relay.setConnectionChangeHandler((connected) => {
    set({ connected, reconnecting: false })
  })

  relay.on('reconnecting', () => {
    set({ reconnecting: true })
  })

  relay.on('paired', (msg: any) => {
    if (msg.desktopName) {
      const state = get()
      set({ desktopName: msg.desktopName })
      persist({ ...state, desktopName: msg.desktopName })
    }
  })

  return {
    channelId: persisted.channelId || '',
    channelKey: persisted.channelKey || '',
    host: persisted.host || '',
    desktopName: persisted.desktopName || '',
    connected: false,
    reconnecting: false,
    paired: persisted.paired || false,

    setPairingInfo: (channelId, channelKey, host) => {
      set({ channelId, channelKey, host, paired: true })
      persist({ channelId, channelKey, host, desktopName: get().desktopName })
    },

    setDesktopName: (name) => {
      set({ desktopName: name })
      const state = get()
      persist({ channelId: state.channelId, channelKey: state.channelKey, host: state.host, desktopName: name })
    },

    setConnected: (connected) => set({ connected }),

    setReconnecting: (reconnecting) => set({ reconnecting }),

    connect: async () => {
      const { channelId, channelKey, host } = get()
      if (!channelId || !channelKey || !host) return
      await relay.connect(channelId, channelKey, host)
    },

    disconnect: () => {
      relay.disconnect()
      set({ connected: false, reconnecting: false })
    },

    unpair: () => {
      relay.disconnect()
      set({
        channelId: '',
        channelKey: '',
        host: '',
        desktopName: '',
        connected: false,
        reconnecting: false,
        paired: false,
      })
      localStorage.removeItem(STORAGE_KEY)
    },
  }
})
